// ============================================================================
// TournamentListCard - the lobby row
// ----------------------------------------------------------------------------
// Trophy, name, then the three numbers a player scans for (prize pool,
// entrants, buy-in), a status bar with the countdown and ONE pill button,
// and the first-place prize underneath. Tap anywhere else to open the
// detail sheet.
// ============================================================================
import React from 'react';
import { Box, HStack, Text, Button, Badge } from '@chakra-ui/react';
import { Trophy, Users, Coins, Medal, CheckCircle2 } from 'lucide-react';
import { POKER_COLORS } from '../../theme/designSystem';
import type { Tournament } from '../../types/tournamentTypes';
import { ActionModel, fmtMoney, prizePoolOf, firstPrizeOf, statusLine, isPlayCoinsTournament } from './tournamentPresenter';

const CARD = '#0f1722';
const BORDER = 'rgba(148,163,184,0.18)';

export interface TournamentListCardProps {
  tournament: Tournament;
  actions: ActionModel;
  now: number;
  isRegistered: boolean;
  busy?: boolean;
  highlighted?: boolean;
  compact?: boolean; // carousel tile
  onOpen: () => void;
  onPrimary: () => void;
}

const toneColor = (tone: ReturnType<typeof statusLine>['tone']) =>
  tone === 'live' ? '#4ade80' : tone === 'warm' ? POKER_COLORS.gold : tone === 'done' ? 'gray.500' : 'gray.300';

export const PrimaryPill: React.FC<{ actions: ActionModel; busy?: boolean; onClick: () => void; full?: boolean }> = ({ actions, busy, onClick, full }) => {
  const p = actions.primary;
  const gold = p.kind === 'register' || p.kind === 'late_reg' || p.kind === 'rebuy';
  const cyan = p.kind === 'join';
  const done = p.kind === 'registered' || p.kind === 'closed';
  return (
    <Button
      onClick={(e) => { e.stopPropagation(); if (!done) onClick(); }}
      isLoading={busy}
      isDisabled={p.disabled}
      minH={full ? '56px' : '44px'} w={full ? '100%' : undefined} px={5} borderRadius="full" fontSize={full ? 'lg' : 'md'} fontWeight="800"
      bg={gold ? POKER_COLORS.gold : cyan ? POKER_COLORS.cyan : done ? 'whiteAlpha.100' : 'whiteAlpha.200'}
      color={gold || cyan ? '#0a1420' : done ? 'gray.300' : 'white'}
      border={done ? '1px solid' : undefined} borderColor="whiteAlpha.300"
      _hover={{ filter: done ? undefined : 'brightness(1.08)' }}
      cursor={done ? 'default' : 'pointer'}
      leftIcon={p.kind === 'registered' ? <CheckCircle2 size={18} /> : undefined}
      title={p.hint}
    >
      {p.label}
    </Button>
  );
};

const TournamentListCard: React.FC<TournamentListCardProps> = ({ tournament: t, actions, now, isRegistered, busy, highlighted, compact, onOpen, onPrimary }) => {
  const { pool, guaranteed, entrants } = prizePoolOf(t);
  const first = firstPrizeOf(t);
  const status = statusLine(t, now);
  const pc = isPlayCoinsTournament(t);
  const reEntries = (t as any).rebuyCount || (t as any).totalRebuys || 0;

  return (
    <Box
      id={`tournament-${t.id}`}
      as="button" textAlign="left" w="100%" display="block"
      bg={CARD} border="1px solid" borderColor={highlighted ? POKER_COLORS.cyan : isRegistered ? `${POKER_COLORS.gold}66` : BORDER}
      borderRadius="2xl" overflow="hidden"
      boxShadow={highlighted ? `0 0 0 3px ${POKER_COLORS.cyan}, 0 0 24px rgba(0,217,217,0.35)` : undefined}
      _hover={{ borderColor: POKER_COLORS.cyan }} _active={{ transform: 'scale(0.995)' }} transition="all 0.15s"
      onClick={onOpen}
      minW={compact ? '300px' : undefined} maxW={compact ? '340px' : undefined} flexShrink={0}
    >
      <Box p={4} pb={3}>
        <HStack align="flex-start" spacing={3}>
          <Box w="56px" h="56px" borderRadius="xl" bg={`${POKER_COLORS.gold}1f`} display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
            <Trophy size={30} color={POKER_COLORS.gold} />
          </Box>
          <Box flex={1} minW={0}>
            <Text color="white" fontWeight="800" fontSize="lg" lineHeight="1.2" noOfLines={2}>
              {fmtMoney(t, t.buyInAmount || 0)} {t.name}
            </Text>
            <HStack spacing={4} mt={2} flexWrap="wrap" rowGap={1}>
              <HStack spacing={1.5} title="Prize pool">
                <Trophy size={16} color={POKER_COLORS.gold} />
                <Text color="white" fontWeight="800" fontSize="md">{fmtMoney(t, pool, { compact: true })}</Text>
                {guaranteed && <Badge bg={`${POKER_COLORS.gold}22`} color={POKER_COLORS.gold} fontSize="9px" borderRadius="full" px={1.5}>GTD</Badge>}
              </HStack>
              <HStack spacing={1.5} title="Entrants">
                <Users size={16} color="#60a5fa" />
                <Text color="white" fontWeight="800" fontSize="md">{entrants}</Text>
              </HStack>
              <HStack spacing={1.5} title="Buy-in">
                <Coins size={16} color={pc ? POKER_COLORS.cyan : '#34d399'} />
                <Text color="white" fontWeight="800" fontSize="md">{fmtMoney(t, t.buyInAmount || 0)}</Text>
              </HStack>
            </HStack>
          </Box>
        </HStack>
      </Box>

      <HStack mx={3} mb={3} bg="whiteAlpha.100" borderRadius="xl" pl={4} pr={1.5} py={1.5} justify="space-between" spacing={3}>
        <Text color={toneColor(status.tone)} fontSize="sm" fontWeight="700" noOfLines={1} flex={1}>
          {status.tone === 'live' && <Box as="span" display="inline-block" w="8px" h="8px" borderRadius="full" bg="#4ade80" mr={2} boxShadow="0 0 8px #4ade80" />}
          {status.text}
        </Text>
        <PrimaryPill actions={actions} busy={busy} onClick={onPrimary} />
      </HStack>

      <HStack px={4} py={2.5} bg="blackAlpha.300" spacing={2} justify="space-between">
        <HStack spacing={2}>
          <Medal size={16} color={POKER_COLORS.gold} />
          <Text color="gray.300" fontSize="sm">1st place</Text>
          <Text color="white" fontWeight="800" fontSize="sm">{fmtMoney(t, first, { compact: true })}</Text>
        </HStack>
        {reEntries > 0 && <Text color="gray.500" fontSize="xs">{reEntries} re-entr{reEntries === 1 ? 'y' : 'ies'}</Text>}
        {actions.isBusted && <Badge bg="rgba(239,68,68,0.18)" color="#fca5a5" borderRadius="full" px={2} fontSize="10px">Busted</Badge>}
      </HStack>
    </Box>
  );
};

export default TournamentListCard;
